/**
 * Proyecto: MOGOTAX-CONDUCTOR
 * Modal de verificación OTP para inicio/fin de traslados
 */
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Input,
  Button,
  Typography,
  Space,
  message,
  Alert,
  Spin,
} from "antd";
import {
  LockOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";

const { Title, Paragraph } = Typography;

const OtpModal = ({
  visible,
  onCancel,
  onVerified,
  trasladoId,
  tipo, // 'inicio' o 'finalizacion'
  loading: externalLoading,
}) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [, setResending] = useState(false);
  const [error, setError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [codeSent, setCodeSent] = useState(false);
  const inputRefs = useRef([]);

  // Solicitar código OTP al abrir el modal
  useEffect(() => {
    if (visible && trasladoId && !codeSent) {
      solicitarCodigo();
    }
  }, [visible, trasladoId]);

  // Countdown para reenvío
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Limpiar estado al cerrar
  useEffect(() => {
    if (!visible) {
      setOtp(["", "", "", "", "", ""]);
      setError(null);
      setCodeSent(false);
    }
  }, [visible]);

  const solicitarCodigo = async () => {
    setResending(true);
    setError(null);

    const endpoint = tipo === 'inicio'
      ? `/conductor/otp/solicitar-inicio/${trasladoId}`
      : `/conductor/otp/solicitar-finalizacion/${trasladoId}`;

    try {
      const response = await api.post(endpoint);
      setCodeSent(true);
      setCountdown(60); // 60 segundos para reenviar
      message.success(response.data?.message || "Código enviado al paciente");
    } catch (err) {
      console.error("Error solicitando OTP:", err);
      setError(err.response?.data?.error || "Error al enviar el código");
    } finally {
      setResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Solo permitir números
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Solo último dígito
    setOtp(newOtp);
    setError(null);

    // Auto-focus al siguiente input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Si se completó el código, verificar automáticamente
    if (index === 5 && value) {
      const codigo = newOtp.join("");
      if (codigo.length === 6) {
        verificarCodigo(codigo);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace para ir al anterior
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
      verificarCodigo(pastedData);
    }
  };

  const verificarCodigo = async (codigo) => {
    if (!codigo || codigo.length !== 6) {
      setError("Por favor ingresa el código completo de 6 dígitos");
      return;
    }

    setLoading(true);
    setError(null);

    const endpoint = tipo === 'inicio'
      ? `/conductor/otp/verificar-inicio/${trasladoId}`
      : `/conductor/otp/verificar-finalizacion/${trasladoId}`;

    try {
      const response = await api.post(endpoint, { codigo });
      message.success(response.data?.message || "Código verificado correctamente");
      onVerified(true);
    } catch (err) {
      console.error("Error verificando OTP:", err);
      const errorMsg = err.response?.data?.error || "Código incorrecto";
      setError(errorMsg);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleReenviar = async () => {
    if (countdown > 0) return;

    try {
      await api.post(`/conductor/otp/reenviar/${trasladoId}`);
      setCountdown(60);
      setOtp(["", "", "", "", "", ""]);
      setError(null);
      message.success("Código reenviado");
      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error("Error reenviando OTP:", err);
      setError(err.response?.data?.error || "Error al reenviar código");
    }
  };

  const handleSubmit = () => {
    const codigo = otp.join("");
    verificarCodigo(codigo);
  };

  const isComplete = otp.every(digit => digit !== "");

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      title={null}
      closable={!loading && !externalLoading}
      maskClosable={false}
      width={360}
    >
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #1890ff, #40a9ff)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}>
          <LockOutlined style={{ fontSize: 32, color: "#fff" }} />
        </div>

        <Title level={4} style={{ marginBottom: 8 }}>
          Verificación OTP
        </Title>

        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          {tipo === 'inicio'
            ? "Ingresa el código de 6 dígitos enviado al paciente para iniciar el traslado"
            : "Ingresa el código de 6 dígitos enviado al paciente para finalizar el traslado"
          }
        </Paragraph>

        {!codeSent ? (
          <div style={{ padding: "20px 0" }}>
            <Spin tip="Enviando código..." />
          </div>
        ) : (
          <>
            {/* Inputs OTP */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 8,
                marginBottom: 24,
              }}
              onPaste={handlePaste}
            >
              {otp.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  maxLength={1}
                  style={{
                    width: 45,
                    height: 50,
                    fontSize: 24,
                    textAlign: "center",
                    fontWeight: "bold",
                    borderColor: error ? "#ff4d4f" : digit ? "#1890ff" : undefined,
                  }}
                  disabled={loading || externalLoading}
                />
              ))}
            </div>

            {/* Error */}
            {error && (
              <Alert
                message={error}
                type="error"
                showIcon
                icon={<CloseCircleOutlined />}
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Botones */}
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              <Button
                type="primary"
                size="large"
                block
                icon={<CheckCircleOutlined />}
                onClick={handleSubmit}
                loading={loading || externalLoading}
                disabled={!isComplete}
              >
                Verificar Código
              </Button>

              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={handleReenviar}
                disabled={countdown > 0 || loading || externalLoading}
              >
                {countdown > 0
                  ? `Reenviar código (${countdown}s)`
                  : "Reenviar código"
                }
              </Button>

              <Button
                type="text"
                onClick={onCancel}
                disabled={loading || externalLoading}
              >
                Cancelar
              </Button>
            </Space>
          </>
        )}
      </div>
    </Modal>
  );
};

OtpModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onVerified: PropTypes.func.isRequired,
  trasladoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tipo: PropTypes.oneOf(["inicio", "finalizacion"]).isRequired,
  loading: PropTypes.bool,
};

OtpModal.defaultProps = {
  loading: false,
};

export default OtpModal;
